export default function ProbateSection() {
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Navigating <strong>Probate</strong> & Lack of Authority
          </h2>
          <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
            Many heirs think they own a house the moment a parent or relative passes away. But legally, the title remains in the deceased's name until probate is complete. <strong>Selling a house in probate with back taxes</strong> or other complications requires special handling - and we specialize in exactly that.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/27778c27472310e85908a5dc6b489515.jpg"
              alt="Probate court documents and legal paperwork"
              className="w-full h-auto rounded-lg shadow-2xl"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">The Probate Authority Gap</h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Here's the problem: You can't sell property you don't legally own. Even if you're the sole heir, even if the will names you as beneficiary, the property title is still in your deceased relative's name. You need legal authority to transfer that title.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              That authority comes through probate. Depending on the situation, you'll need:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <i className="ri-file-list-3-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700"><strong>Letters of Administration:</strong> If there's no will (intestate succession)</p>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-file-list-3-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700"><strong>Letters Testamentary:</strong> If there's a will and you're named executor</p>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-file-list-3-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700"><strong>Executor's Deed:</strong> The legal document that transfers title from the estate to the buyer</p>
              </li>
            </ul>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Traditional probate in Texas takes 6-12 months minimum. During that time, you're responsible for property taxes, insurance, maintenance, and utilities - even though you can't sell the property yet.
            </p>
            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
              <p className="text-gray-900 font-semibold mb-2">Common Mistake:</p>
              <p className="text-gray-700">
                Trying to sell before probate is complete. This wastes everyone's time and can create legal liability. We help you understand exactly where you are in the process and what needs to happen before closing.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-8 md:p-12 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Types of Probate in Texas
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-emerald-500 transition-colors">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Independent Administration</h4>
              <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                The fastest and least expensive option. The executor can act without court approval for most decisions.
              </p>
              <p className="text-xs text-emerald-600 font-semibold">Timeline: 6-9 months</p>
            </div>
            <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-emerald-500 transition-colors">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Dependent Administration</h4>
              <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                Requires court approval for major decisions. More expensive and time-consuming than independent administration.
              </p>
              <p className="text-xs text-emerald-600 font-semibold">Timeline: 12-18 months</p>
            </div>
            <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-emerald-500 transition-colors">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Muniment of Title</h4>
              <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                Simplified process when there are no debts except secured by real estate. The will itself serves as proof of ownership.
              </p>
              <p className="text-xs text-emerald-600 font-semibold">Timeline: 3-6 months</p>
            </div>
            <div className="border-2 border-gray-200 rounded-lg p-6 hover:border-emerald-500 transition-colors">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Small Estate Affidavit</h4>
              <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                For estates under $75,000 with no will. Allows transfer without formal probate through an affidavit.
              </p>
              <p className="text-xs text-emerald-600 font-semibold">Timeline: 1-3 months</p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Intestate Succession: Dying Without a Will</h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              When someone dies without a will in Texas, state law determines who inherits. This is called <strong>intestate succession</strong>. The property doesn't automatically go to the spouse or children - it depends on the family structure.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Texas intestate succession rules:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700"><strong>Married with children:</strong> Spouse gets 1/3, children get 2/3 (or spouse gets all community property, children get separate property)</p>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700"><strong>Married, no children:</strong> Spouse inherits everything</p>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700"><strong>Unmarried with children:</strong> Children inherit everything equally</p>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-line text-2xl text-emerald-600 flex-shrink-0"></i>
                <p className="text-gray-700"><strong>No spouse or children:</strong> Parents inherit, then siblings, then more distant relatives</p>
              </li>
            </ul>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Intestate succession creates complex ownership situations. Multiple heirs with fractional interests, some who want to sell and some who don't. This is where <strong>selling inherited property with liens</strong> becomes even more complicated.
            </p>
            <div className="bg-emerald-500 text-white p-6 rounded-lg">
              <h4 className="font-bold text-xl mb-3">We Buy Properties in Intestate Estates</h4>
              <p className="leading-relaxed">
                Even if probate isn't complete, we can work with you to structure a deal that closes as soon as you have legal authority. We'll wait for your Letters of Administration and close immediately after they're issued.
              </p>
            </div>
          </div>
          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=stressed%20heir%20sitting%20at%20desk%20surrounded%20by%20stacks%20of%20estate%20paperwork%20probate%20documents%20bills%20and%20legal%20forms%20looking%20overwhelmed%20calculator%20and%20coffee%20cup%20on%20desk%20inheritance%20burden%20concept%20natural%20lighting&width=800&height=1000&seq=titleissues007&orientation=portrait"
              alt="Heir overwhelmed by probate paperwork and estate documents"
              className="w-full h-auto rounded-lg shadow-2xl"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-8 md:p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            The Hidden Costs of Waiting for Probate
          </h3>
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-4xl text-red-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-2">$8,400</h4>
              <p className="text-gray-700">Average carrying costs during 12-month probate (taxes, insurance, utilities, maintenance)</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-scales-3-line text-4xl text-red-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-2">$5,000-$15,000</h4>
              <p className="text-gray-700">Attorney fees for probate administration in Texas</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-4xl text-red-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-2">6-18 Months</h4>
              <p className="text-gray-700">Time lost waiting for probate to complete before you can sell</p>
            </div>
          </div>
          <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-lg">
            <h4 className="font-bold text-gray-900 text-xl mb-3">Our Probate-Friendly Approach:</h4>
            <p className="text-gray-700 leading-relaxed mb-4">
              We understand probate timelines and requirements. We can give you a cash offer now, then wait for your probate to complete before closing. This gives you certainty and peace of mind while you navigate the legal process.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We've closed deals with executors, administrators, and heirs in all types of probate situations. We know how to work with probate attorneys and courts to ensure a smooth closing. And if the property has <strong>back taxes, liens, or other complications</strong>, we handle those at closing so you don't have to worry about them.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}